import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Shield, User, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const TeamManagement = ({ token, API_BASE_URL }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', email: '', phone: '', role: 'agent' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/team/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (err) {
      console.error('Failed to fetch team members', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/team/`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newMember)
      });
      
      if (response.ok) {
        await fetchMembers();
        setIsModalOpen(false);
        setNewMember({ name: '', email: '', phone: '', role: 'agent' });
      }
    } catch (err) {
      console.error('Failed to create member', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to remove this agent? Leads assigned to them will not be deleted.')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/team/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) fetchMembers();
    } catch (err) {
      console.error(err);
    }
  };

  const toggleActive = async (member) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/team/${member.id}`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !member.is_active })
      });
      if (response.ok) fetchMembers();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><Loader2 className="w-8 h-8 animate-spin text-gold-500" /></div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Users className="w-8 h-8 text-gold-500" />
            Team Management
          </h1>
          <p className="text-gray-400 mt-2">Manage your agents. Incoming Zapier leads will be Round-Robin distributed among active agents.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="btn-gold flex items-center gap-2"
        >
          <Plus size={20} />
          Add Agent
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {members.map(member => (
          <div key={member.id} className={`glass-card p-6 border-t-4 transition-all ${member.is_active ? 'border-t-gold-500' : 'border-t-gray-600 opacity-60'}`}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-navy-800 flex items-center justify-center">
                  <User className="w-6 h-6 text-gray-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{member.name}</h3>
                  <span className="text-xs px-2 py-1 rounded bg-navy-800 text-gold-400 capitalize">{member.role}</span>
                </div>
              </div>
              <button onClick={() => handleDelete(member.id)} className="text-gray-500 hover:text-red-400 transition-colors">
                <Trash2 size={18} />
              </button>
            </div>
            
            <div className="space-y-2 mb-6">
              <p className="text-sm text-gray-400">📧 {member.email || 'No email provided'}</p>
              <p className="text-sm text-gray-400">📱 {member.phone || 'No phone provided'}</p>
            </div>

            <button 
              onClick={() => toggleActive(member)}
              className={`w-full py-2 rounded-lg flex justify-center items-center gap-2 text-sm font-medium transition-colors ${
                member.is_active ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {member.is_active ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
              {member.is_active ? 'Active (Receiving Leads)' : 'Inactive (Paused)'}
            </button>
          </div>
        ))}
      </div>

      {members.length === 0 && (
        <div className="text-center py-20 glass-card">
          <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">No agents found</h2>
          <p className="text-gray-400">Add your first team member to start distributing leads.</p>
        </div>
      )}

      {/* Add Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-navy-900 border border-white/10 rounded-2xl w-full max-w-md p-6 animate-fade-in-up">
            <h2 className="text-2xl font-bold text-white mb-6">Add Team Member</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Full Name</label>
                <input required type="text" value={newMember.name} onChange={e => setNewMember({...newMember, name: e.target.value})} className="input-field" placeholder="e.g. John Doe" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email (Optional)</label>
                <input type="email" value={newMember.email} onChange={e => setNewMember({...newMember, email: e.target.value})} className="input-field" placeholder="john@agency.com" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Phone (Optional)</label>
                <input type="text" value={newMember.phone} onChange={e => setNewMember({...newMember, phone: e.target.value})} className="input-field" placeholder="+971..." />
              </div>
              <div className="flex gap-3 mt-8">
                <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 py-3 px-4 rounded-xl text-gray-400 hover:bg-white/5 transition-colors">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 btn-gold flex justify-center items-center">
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Agent'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
